import type { OriginalsConfig, ParamSpec, ParamValue } from '../types.js';
import type { BetLimits } from '../bet-panel/AmountField.js';

export function defaultParams(schema: ParamSpec[]): Record<string, ParamValue> {
  return Object.fromEntries(schema.map((s) => [s.key, s.default]));
}

export function betLimitsOf(config: OriginalsConfig): BetLimits {
  const bl = config.betLimits;
  if ('options' in bl) {
    const opts = bl.options.map((o) => BigInt(o));
    return { min: opts[0] ?? 0n, max: opts[opts.length - 1] ?? 0n, step: 1n };
  }
  return { min: BigInt(bl.min), max: BigInt(bl.max), step: bl.step ? BigInt(bl.step) : 1n };
}

/**
 * Bare decimal string for EDITABLE money fields (no currency symbol, no thousands
 * grouping) — so the value round-trips back through `useMoney().toMinor` without
 * Intl artifacts. `useMoney().fromMinor` returns a full currency string ("THB 1,234.50")
 * which breaks re-parsing; use this for inputs + numeric displays, fromMinor only for
 * read-only labels. `multiplier` = `useMoney().multiplier()` (e.g. 100n THB, 1n JPY).
 */
export function minorToPlain(minor: bigint, multiplier: bigint): string {
  if (multiplier <= 1n) return minor.toString();
  const dp = multiplier.toString().length - 1;
  const neg = minor < 0n;
  const abs = neg ? -minor : minor;
  const whole = abs / multiplier;
  const frac = (abs % multiplier).toString().padStart(dp, '0');
  return `${neg ? '-' : ''}${whole}.${frac}`;
}
